import { AxiosInstance, HttpStatusCode, InternalAxiosRequestConfig } from 'axios';
// The leaf module, not the jwt barrel or getTokenExpirationMs, which imports it:
// the barrel brings in jose, whose ESM build fails every Jest suite that loads
// this interceptor unmocked.
import { decodeJwt } from '@/src/shared/lib/jwt/decodeJwt';
import type { RefreshResult } from '../auth/clientSession';

// Headroom so a token that is still valid when read does not expire in flight.
const CREDENTIAL_CHECK_MARGIN_MS = 30_000;

// Marks a request that already went through one refresh+retry cycle so a second
// 401 on the same request falls through to logout instead of looping.
type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean };

declare module 'axios' {
  export interface AxiosRequestConfig {
    // Set it when a 401 from this endpoint means the credential in the body is
    // wrong rather than the session expired, so the error reaches the caller
    // instead of the logout path.
    credentialCheck?: boolean;
  }
}

// Module-level single-flight: a burst of concurrent 401s shares ONE refresh
// call rather than hammering /api/auth/refresh once per in-flight request.
let refreshPromise: Promise<RefreshResult> | null = null;

export const withAuth = (
  client: AxiosInstance,
  deps: {
    getAccessToken: () => Promise<string | null>;
    refreshAccessToken?: () => Promise<RefreshResult>;
    onUnauthorized?: () => Promise<void> | void;
  },
) => {
  async function refreshOnce(): Promise<RefreshResult> {
    refreshPromise = refreshPromise ?? deps.refreshAccessToken!();
    try {
      return await refreshPromise;
    } finally {
      refreshPromise = null;
    }
  }

  client.interceptors.request.use(async (config) => {
    let token = await deps.getAccessToken();
    // A credential check's 401 cannot be told apart from an expired session, so
    // the response path never refreshes it. Renewing an expiring token up front
    // is the only way that request ever gets a live one.
    if (config.credentialCheck) {
      const exp = token ? decodeJwt<{ exp?: number }>(token)?.exp : undefined;
      const expiring = exp !== undefined && exp * 1000 - CREDENTIAL_CHECK_MARGIN_MS <= Date.now();
      if ((!token || expiring) && deps.refreshAccessToken) {
        const result = await refreshOnce();
        if (result.kind === 'ended') {
          await deps.onUnauthorized?.();
        }
        token = result.kind === 'token' ? result.token : null;
      }
      // Sent without a live token, the 401 would read as a wrong credential, so
      // the request never goes out. A transient renewal or a token read that
      // failed does not log out: neither proves the session is gone.
      if (!token) {
        throw new Error('No live session for a credential check');
      }
    }
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });

  client.interceptors.response.use(
    (res) => res,
    async (error) => {
      const status = error.response?.status;
      const config = error.config as RetriableConfig | undefined;

      const sessionBound = status === HttpStatusCode.Unauthorized && !config?.credentialCheck;

      const canRetry = sessionBound && !!config && !config._retried && !!deps.refreshAccessToken;

      if (canRetry) {
        config!._retried = true;

        const result = await refreshOnce();

        if (result.kind === 'token') {
          config!.headers.Authorization = `Bearer ${result.token}`;
          return client(config!);
        }
        // The backend could not be asked, so this 401 does not prove the
        // session ended; the next request tries the renewal again.
        if (result.kind === 'transient') {
          throw error;
        }
      }

      if (sessionBound) {
        await deps.onUnauthorized?.();
      }

      throw error;
    },
  );

  return client;
};
